import {
  Box,
  Divider,
  Flex,
  Group,
  Table as MantineTable,
  NumberInput,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm, UseFormReturnType } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import { Fragment, useEffect } from 'react';
import type { Spending } from '../../../api/financials-client/financials-client.type';
import type { Project } from '../../../api/projects-client/projects-client.type';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { useFinancialsStore } from '../../../store/financials-store/financials-store';
import { setTableSearchTerm } from '../../../store/financials-store/financials-store.actions';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { baseWhite, inkLight, skyDark, skyLightest } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { toast } from '../../notifications';
import { usePortfolioProjectsAndBoards, useSaveSpendingMutation } from '../financials.hooks';
import { buildSpendingPayload, filterProjectsBySearch } from './helpers';

dayjs.extend(localeData);

type SpendingForm = Spending & { key: string };

function Table({ spendingData, isLoading }: { spendingData: Spending[]; isLoading: boolean }) {
  const portfolio = useGlobalStore((s) => s.portfolio)!;
  const selectedYear = useFinancialsStore((s) => s.table.selectedYear);
  const searchTerm = useFinancialsStore((s) => s.table.searchTerm);
  const { data: projects } = usePortfolioProjectsAndBoards(portfolio.id);

  const saveMutation = useSaveSpendingMutation();

  const monthLabels = dayjs().localeData().monthsShort();

  const form = useForm<{ expenditures: SpendingForm[] }>({
    initialValues: {
      expenditures:
        spendingData && spendingData.length > 0
          ? spendingData
              .filter((e) => Boolean(e.subproject_id))
              .map((e) => ({
                key: `${e.project_id}-${e.subproject_id}-${e.year}-${e.month}`,
                ...e,
              }))
          : [],
    },
  });

  // if the API payload changes, we need to reinitialize the form with fresh data
  // biome-ignore lint/correctness/useExhaustiveDependencies: <adding form.initialize here causes an infinite render loop>
  useEffect(() => {
    if (spendingData && spendingData.length > 0) {
      form.resetDirty();
      form.initialize({
        expenditures: spendingData
          .filter((e) => Boolean(e.subproject_id))
          .map((e) => ({
            key: `${e.project_id}-${e.subproject_id}-${e.year}-${e.month}`,
            ...e,
          })),
      });
    }
  }, [spendingData]);

  const filteredProjects = projects ? filterProjectsBySearch(projects, searchTerm ?? '') : [];

  return (
    <Fragment>
      <BrandedLoadingOverlay visible={isLoading} variant="colored" />
      <Stack>
        <Group justify="space-between">
          <Group>
            <Text fw={700} size="lg">
              All Projects
            </Text>
          </Group>
          <Group>
            {form.isDirty() && (
              <Group>
                <Button
                  variant="primary"
                  onClick={() => {
                    const expenditures = form.getValues().expenditures;
                    const payload = buildSpendingPayload(expenditures, selectedYear);
                    if (payload) {
                      saveMutation.mutate(payload, {
                        onSuccess: () => {
                          toast.success('Your data has been saved successfully!');
                        },
                        onError: () => {
                          toast.error('There was an error saving your data. Please try again.');
                        },
                      });
                    }
                  }}
                  radius="xl"
                  size="xs"
                  leftSection={<Icon name="check_circle" color={baseWhite} size={16} />}
                >
                  Save Changes
                </Button>
                <Button
                  variant="outline"
                  onClick={() => form.reset()}
                  radius="xl"
                  size="xs"
                  leftSection={<Icon name="cancel" color={inkLight} size={16} />}
                >
                  Cancel
                </Button>
              </Group>
            )}
            <TextInput
              placeholder={'Search for Project or Board'}
              value={searchTerm ?? ''}
              onChange={(e) => setTableSearchTerm(e.target.value)}
              leftSection={<Icon name="search" />}
              radius="xl"
              style={{ width: 400 }}
            />
          </Group>
        </Group>
        <Divider />
        <MantineTable verticalSpacing="5" withRowBorders={false} style={{ tableLayout: 'fixed', minWidth: 800 }}>
          {' '}
          <colgroup>
            <col style={{ width: '100%' }} />
            {monthLabels.map((label, index) => (
              <col
                key={label}
                style={{
                  backgroundColor:
                    selectedYear === dayjs().year() && dayjs().month() + 1 <= index ? skyLightest : baseWhite,
                }}
              />
            ))}
          </colgroup>
          <ScrollArea w="100%" h="100%" offsetScrollbars type="always" scrollbarSize={10} scrollbars="x">
            <Box w="100%">
              <MantineTable.Thead>
                <MantineTable.Tr py={24}>
                  <MantineTable.Th style={{ minWidth: 200 }}>
                    <Text fw={700}>Project / Board</Text>
                  </MantineTable.Th>
                  {monthLabels.map((m) => (
                    <MantineTable.Th
                      key={m}
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      <Text c={skyDark}>{m}</Text>
                    </MantineTable.Th>
                  ))}
                </MantineTable.Tr>
              </MantineTable.Thead>
              <MantineTable.Tbody>
                {selectedYear &&
                  filteredProjects.map((project) => (
                    <ProjectRows
                      key={project.id}
                      portfolioId={portfolio.id}
                      projectId={project.id}
                      selectedYear={selectedYear}
                      projectsData={filteredProjects}
                      form={form}
                    />
                  ))}
              </MantineTable.Tbody>
            </Box>
          </ScrollArea>
        </MantineTable>
      </Stack>
    </Fragment>
  );
}

function ProjectRows({
  portfolioId,
  projectId,
  selectedYear,
  projectsData,
  form,
}: {
  portfolioId: string;
  projectId: string;
  selectedYear: number;
  projectsData: Project[];
  form: UseFormReturnType<{ expenditures: SpendingForm[] }>;
}) {
  const [isOpen, { toggle }] = useDisclosure(true);
  const project = projectsData.find((p: any) => p.id === projectId);
  if (!project) return null;

  const expenditures = form.values.expenditures;

  // helper to roll up totals
  const rollUp = (month: number) => {
    const rows = expenditures.filter(
      (r) => r.project_id === projectId && r.year === selectedYear && r.subproject_id !== null && r.month === month,
    );
    if (!rows.length || rows.every((r) => r.budget == null)) return null;
    return rows.reduce((sum, r) => sum + (r.budget || 0), 0);
  };

  const rows = [
    <MantineTable.Tr key={project.id} py={24}>
      <MantineTable.Th>
        <Flex>
          <Icon
            name={isOpen ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
            onClick={toggle}
            style={{ cursor: 'pointer' }}
            color="black"
          />
          <Text fw={700}>{project.name}</Text>
        </Flex>
      </MantineTable.Th>
      {dayjs()
        .localeData()
        .months()
        .map((_, i) => (
          <MantineTable.Th key={i}>
            <Text fw={700} size="sm">
              {rollUp(i + 1)}
            </Text>
          </MantineTable.Th>
        ))}
    </MantineTable.Tr>,
  ];

  // detail rows
  if (isOpen) {
    rows.push(
      ...project.subprojects
        .map((sp) => [sp.id, sp.name] as const)
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([boardId, boardName]) => (
          <MantineTable.Tr key={`${projectId}-${boardId}`} py={24}>
            <MantineTable.Td pl={48}>{boardName}</MantineTable.Td>
            {dayjs()
              .localeData()
              .months()
              .map((_, idx) => {
                const year = selectedYear;
                const month = idx + 1;
                const key = `${projectId}-${boardId}-${year}-${month}`;
                const i = expenditures.findIndex((r) => r.key === key);

                // existing row → controlled input
                if (i >= 0) {
                  return (
                    <MantineTable.Td key={month}>
                      <NumberInput
                        hideControls
                        style={{ width: 90 }}
                        {...form.getInputProps(`expenditures.${i}.budget`)}
                        key={key}
                        radius="md"
                      />
                    </MantineTable.Td>
                  );
                }

                // new row → blank input that inserts on first change
                return (
                  <MantineTable.Td key={month}>
                    <NumberInput
                      // already added 1 to month to make it human readable, so add 2 to disable next month and beyond
                      disabled={selectedYear === dayjs().year() && dayjs().month() + 2 <= month}
                      hideControls
                      style={{ width: 90 }}
                      value={undefined}
                      onChange={(value) => {
                        const newBudget = value === '' || value === undefined ? null : Number(value);
                        form.insertListItem('expenditures', {
                          key,
                          project_id: projectId,
                          subproject_id: boardId,
                          portfolio_id: portfolioId,
                          year,
                          month,
                          budget: newBudget,
                          valid: true,
                          autofilled: false,
                        });
                      }}
                      key={key}
                      radius="md"
                    />
                  </MantineTable.Td>
                );
              })}
          </MantineTable.Tr>
        )),
    );
  }

  return rows;
}

export { Table };
